import React, { useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import useAuthStore from "Store/useAuthStore";
import { JD_STATUSES, getStatusTooltip, TOOLTIP_STATUS_PROCESSING_FAILED, JD_STATUS_FILTERS, jdStatusOnFilter } from "constants/jobs.constants";
import useJobsStore from "Store/useJobsStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { AuthContext } from "State/AuthContext";
import { buildFieldConfigMap } from "Utils/formatting";
import Permission from "Components/Permission";
import { JobsTableWrapperStyled } from "./JobsLibraryV2.styles";
import { TOUR_COLUMN_CLASS } from "./JobsLibraryV2.constants";
import SbTable from "uiCore/SbTable/SbTable";
import SbCheckbox from "uiCore/SbCheckbox/SbCheckbox";
import SbTag from "uiCore/SbTag/SbTag";
import { FilterOutlined, SbTooltip } from "uiCore";
import SbModal from "uiCore/SbModal/SbModal";
import SbButton from "uiCore/SbButton/SbButton";
import { retryJdGeneration } from "Api/apiService";
import { showNotification } from "Store/actions/notificationActions";

// PRD §5.1 — tooltip content strings for Jobs Library row elements.
// Status tooltips (getStatusTooltip, TOOLTIP_STATUS_PROCESSING_FAILED) live in
// constants/jobs.constants -- shared with Compensation Management so both
// surfaces describe each jd_status identically.
const TOOLTIP_CRITICAL_ROLE = "Mark this role as business-priority. Priority roles are prioritised in skills planning and highlighted in the library.";
const TOOLTIP_RETRY = "This JD could not be generated. Click to retry — the AI will attempt to regenerate the job description.";
const TOOLTIP_PENDING_APPROVAL_ROW = "This JD is awaiting your approval. Click to open the comparison view and approve or request revisions.";

const RequisitionViewTableV2 = ({
  loading,
  setLoading,
  filteredJobs,
  handleRowClick,
  handleCheckboxChange,
  jobTaskId,
  messages,
  setJobTaskId,
}) => {
  const dispatch = useDispatch();
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const pendingJdId = useJobsStore((state) => state.pendingJdId);
  const { entitlements } = useContext(AuthContext);
  const canRetryJd = useMemo(
    () => entitlements?.find(e => e.name === "jobs_library")?.entitlements?.some(a => a === "retry_jd" || a === "*") ?? false,
    [entitlements]
  );
  const [pendingAnalysisRetryJdModal, setPendingAnalysisRetryJdModal] = useState(false);
  const [generationFailedModel, setGenerationFailedModel] = useState(false);
  const [retryInProgressModal, setRetryInProgressModal] = useState(false);
  const [retryJobId, setRetryJobId] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const handleRetryJd = async () => {
    if (!retryJobId) return;
    setRetryLoading(true);
    try {
      await retryJdGeneration(retryJobId, email);
      dispatch(showNotification("JD generation retried successfully", "success"));
      setJobTaskId?.({ task_id: retryJobId, job_id: retryJobId });
      setLoading(true);
    } catch {
      dispatch(showNotification("Failed to retry JD generation. Please try again.", "error"));
    } finally {
      setRetryLoading(false);
      setPendingAnalysisRetryJdModal(false);
      setRetryJobId(null);
    }
  };


  // Build a fieldMap keyed by field_name for the 'req_no' ui_element.
  // Using find-by-type (not hardcoded index) so labels remain correct if the
  // server ever reorders ui_elements. All field_names in req_no are unique so
  // a flat map is safe here — no duplicate-key risk.
  const fieldMap = useMemo(
    () => buildFieldConfigMap(orgMetaData, 'req_no'),
    [orgMetaData],
  );
  const isSubFunctionVisible = fieldMap?.sub_function?.visible !== false;

  const getFilters = (key) =>
    [...new Set((filteredJobs || []).map((item) => item[key]).filter(Boolean))]
      .sort((a, b) => {
        const aNum = /^\d/.test(a);
        const bNum = /^\d/.test(b);
        if (aNum && !bNum) return -1;
        if (!aNum && bNum) return 1;
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      })
      .map((value) => ({ text: value, value }));

  const columns = [
    {
      title: 'Status',
      dataIndex: 'jd_status',
      onHeaderCell: () => ({ className: TOUR_COLUMN_CLASS.status }),
      filters: JD_STATUS_FILTERS,
      onFilter: jdStatusOnFilter,
      render: (status, record) => {
        const isFailed = status === JD_STATUSES.PENDING_ANALYSIS || status === JD_STATUSES.GENERATION_FAILED;
        const taskProgress = Number(messages?.progress || 0);
        const isTaskRow =
          jobTaskId?.job_id && jobTaskId?.task_id &&
          (jobTaskId.job_id === record.job_id || jobTaskId.task_id === record.sb_unique_id);
        // At progress 100 the row is still PENDING_ANALYSIS for a few seconds while BigQuery
        // catches up — that is generation finishing, not a failure, so don't flash red.
        const showProcessingFailed = isFailed && (!isTaskRow || taskProgress === 0 || (taskProgress === 100 && status !== JD_STATUSES.PENDING_ANALYSIS));
        // Tooltip content reflects the underlying jd_status (PRD §5.1).
        const tooltipContent = showProcessingFailed
          ? TOOLTIP_STATUS_PROCESSING_FAILED
          : getStatusTooltip(status);

        return (
          <SbTooltip content={tooltipContent}>
            <SbTag color={status === JD_STATUSES.PUBLISHED ? 'success' : showProcessingFailed ? 'error' : 'processing'}>
              {status === JD_STATUSES.PUBLISHED ? 'Published' : showProcessingFailed ? 'Processing Failed' : 'Unpublished'}
            </SbTag>
          </SbTooltip>
        );
      },
    },
    {
      title: fieldMap['job_code']?.label,
      dataIndex: "sb_requisition_number",
      filters: getFilters("sb_requisition_number"),
      onFilter: (value, record) => record.sb_requisition_number === value,
    },
    {
      title: fieldMap['job_title']?.label,
      dataIndex: "job_title",
      render: (_, record) => record.job_title_for_jd || record.job_title || "",
      filters: getFilters("job_title"),
      onFilter: (value, record) => record.job_title === value,
    },
    {
      title: fieldMap['business_unit']?.label,
      dataIndex: "business_unit",
      filters: getFilters("business_unit"),
      onFilter: (value, record) => record.business_unit === value,
    },
    {
      title: fieldMap['location']?.label,
      dataIndex: "location",
      filters: getFilters("location"),
      onFilter: (value, record) => record.location === value,
    },
    {
      title: fieldMap['function']?.label,
      dataIndex: "function",
      filters: getFilters("function"),
      onFilter: (value, record) => record.function === value,
    },
    ...(isSubFunctionVisible
      ? [
        {
          title: fieldMap['sub_function']?.label,
          dataIndex: "sub_function",
          filters: getFilters("sub_function"),
          onFilter: (value, record) => record.sub_function === value,
        },
      ]
      : []),
    {
      title: fieldMap['job_level']?.label,
      dataIndex: "job_level",
      filters: getFilters("job_level"),
      onFilter: (value, record) => record.job_level === value,
    },
    {
      title: fieldMap['version']?.label,
      dataIndex: "version_info",
      filters: getFilters("version_info"),
      onFilter: (value, record) => record.version_info === value,
    },
    {
      title: fieldMap['critical_role']?.label,
      dataIndex: "critical_role",
      align: "center",
      onHeaderCell: () => ({ className: TOUR_COLUMN_CLASS.criticalRole }),
      filters: [
        { text: "Priority", value: true },
        { text: "Non-Priority", value: false },
      ],
      onFilter: (value, record) =>
        (record.critical_role === true || record.critical_role === "true") === value,
      render: (_, record) => (
        <Permission resource="jobs_library" actions={["mark_critical_role"]}>
          <SbTooltip content={TOOLTIP_CRITICAL_ROLE}>
            <SbCheckbox
              checked={record.critical_role === true || record.critical_role === "true"}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleCheckboxChange(e, record.sb_unique_id)}
            />
          </SbTooltip>
        </Permission>
      ),
    },
  ];

  const getRowClassName = (record) => {
    const isLoadingRow = record.job_id && record.job_id === pendingJdId;
    const isJobApprovalPending =
      record.jd_status === "PENDING_APPROVAL" && record.assigned_approver_email === email;
    const isJobNeedRevision =
      record.jd_status === "NEEDS_REVISION" && record.user_id === email;
    const taskProgress = Number(messages?.progress || 0);
    const isTaskRow =
      jobTaskId?.job_id && jobTaskId?.task_id &&
      (jobTaskId.job_id === record.job_id || jobTaskId.task_id === record.sb_unique_id);
    const showProgressBar = isTaskRow && ((taskProgress > 0 && taskProgress < 100) || (taskProgress === 100 && record?.jd_status === "GENERATED"));

    let cls;
    if (isLoadingRow) {
      cls = "jobs-table-content jd-in-progress-row";
    } else if (isJobApprovalPending) {
      cls = "jobs-table-content job-approval-pending";
    } else if (isJobNeedRevision) {
      cls = "jobs-table-content needs-revision-jd-row";
    } else if (record.jd_status !== "PUBLISHED") {
      cls = "jobs-table-content jd-unpublished";
    } else {
      cls = "jobs-table-content";
    }

    return `${cls}${showProgressBar ? " fill-bg" : ""}`;
  };

  const getRowTitle = (record) => {
    const isLoadingRow = record.job_id && record.job_id === pendingJdId;
    const isJobApprovalPending =
      record.jd_status === "PENDING_APPROVAL" && record.assigned_approver_email === email;
    const isTaskRow =
      jobTaskId?.job_id && jobTaskId?.task_id &&
      (jobTaskId.job_id === record.job_id || jobTaskId.task_id === record.sb_unique_id);
    const isRetryableFailure =
      (record?.jd_status === JD_STATUSES.GENERATION_FAILED ||
        record?.jd_status === JD_STATUSES.PENDING_ANALYSIS) && canRetryJd;

    // PRD §5.1 — Assigned approver sees the awaiting-approval prompt.
    if (isJobApprovalPending) return TOOLTIP_PENDING_APPROVAL_ROW;
    if (isLoadingRow) return "Thinking...";
    if (isTaskRow) return Number(messages?.progress) === 100 ? (record?.jd_status === JD_STATUSES.GENERATION_FAILED ? 'Processing Failed' : (messages?.extra_info?.is_retrying ? 'JD Retried' : 'JD Created')) : `${messages?.extra_info?.is_retrying ? "Retrying" : "Creating"} JD - ${messages?.status || ''}`;
    // PRD §5.1 — Retry-eligible failure rows surface the retry prompt for users
    // with the retry_jd entitlement.
    if (isRetryableFailure) return TOOLTIP_RETRY;
    return "View and Edit Job Description";
  };

  return (
    <JobsTableWrapperStyled>
      <SbTable
        columns={columns}
        dataSource={filteredJobs}
        rowKey={(record, index) => record.sb_unique_id ?? index}
        loading={loading}
        sticky
        rowClassName={getRowClassName}
        pagination={{
          showTotal: (total) => {
            const hasActiveFilters = total < (filteredJobs?.length ?? 0);
            return (
              <div className="paic-pagination-total">
                <FilterOutlined />
                <span className={hasActiveFilters ? "is-active-text" : "is-empty-text"}>
                  {hasActiveFilters
                    ? `${total} Roles selected`
                    : "0 Roles selected. Please select at least one role"}
                </span>
              </div>
            );
          },
        }}
        onRow={(record) => {
          const isLoadingRow = record.job_id && record.job_id === pendingJdId;
          const isPendingAnalysisOrGenerationFailed = record?.jd_status === JD_STATUSES.PENDING_ANALYSIS || record?.jd_status === JD_STATUSES.GENERATION_FAILED;
          const taskProgress = Number(messages?.progress || 0);
          const isTaskRow =
            jobTaskId?.job_id && jobTaskId?.task_id &&
            (jobTaskId.job_id === record.job_id || jobTaskId.task_id === record.sb_unique_id);
          return {
            title: getRowTitle(record),
            style: { "--progress": `${messages?.progress}%` },
            onClick: () => {
              if (isLoadingRow) return;
              if (!isPendingAnalysisOrGenerationFailed) {
                handleRowClick(record);
              } else if (taskProgress > 0 && taskProgress < 100) {
                if (isTaskRow) return;
                setRetryInProgressModal(true);
              } else if (canRetryJd) {
                setRetryJobId(record.sb_unique_id);
                setPendingAnalysisRetryJdModal(true);
              } else {
                setGenerationFailedModel(true);
              }
            },
          };
        }}
      />
      {/* Retry JD Generation — users with retry_jd entitlement */}
      <SbModal
        open={pendingAnalysisRetryJdModal}
        title="Retry JD Generation"
        onCancel={() => { setPendingAnalysisRetryJdModal(false); setRetryJobId(null); }}
        footer={
          <>
            <SbButton onClick={() => { setPendingAnalysisRetryJdModal(false); setRetryJobId(null); }}>
              Cancel
            </SbButton>
            <SbButton buttonType="primary" loading={retryLoading} onClick={handleRetryJd}>
              Retry JD
            </SbButton>
          </>
        }
      >
        This job description could not be generated. Would you like to retry the JD generation?
      </SbModal>

      {/* Retry already in progress */}
      <SbModal
        open={retryInProgressModal}
        title="Retry Already In Progress"
        onCancel={() => setRetryInProgressModal(false)}
        footer={
          <SbButton buttonType="primary" onClick={() => setRetryInProgressModal(false)}>
            OK
          </SbButton>
        }
      >
        One JD is already being retried. Please wait for it to complete.
      </SbModal>

      {/* JD Generation Failed — users without retry_jd entitlement */}
      <SbModal
        open={generationFailedModel}
        title="JD Generation Failed"
        onCancel={() => { setGenerationFailedModel(false); setRetryJobId(null); }}
        footer={
          <SbButton buttonType="primary" onClick={() => { setGenerationFailedModel(false); setRetryJobId(null); }}>
            OK
          </SbButton>
        }
      >
        This job description could not be generated. Please contact SkillsBridge support to retry the analysis.
      </SbModal>
    </JobsTableWrapperStyled>
  );
};

export default RequisitionViewTableV2;
